import * as THREE from 'three';
import { Core } from './core/Core';
import { LightingService } from './core/LightingService';
import { CameraService } from './core/CameraService';
import { TickService } from './core/TickService';

export class Scene {
	/**
	 * System Properties
	 * */
	core: Core;
	triangles: Triangle[] = [];
	autoMouseMovePlaying: boolean = true;
	autoMouseMovePoints: number[][] = [[-0.5, 0], [1.5, 0.25], [0.5, 1.5], [0.75, -0.5], [-0.5, 0]];
	autoMouseMoveActivePoints: number[][] = JSON.parse(JSON.stringify(this.autoMouseMovePoints));
	autoMouseMovePoint: number[] = [0.5, 1];

	/**
	 * Responsive
	 */
	//boundingBoxZoomInterval: any;
	//boundingBoxCheckRateMS: number = 2500;

	/**
	 * Properties
	 * */
	constructor(core: Core) {
		this.core = core;

		// Create Lights

		/*const ambientLight = sLighting.createAmbientLight("WorldAmbient", "#ffffff", 3);
		sLighting.enableLight(ambientLight);*/
		this.setDirectionalLight();

		// Create cameras
		const sCamera: CameraService = this.core.s('Camera');
		sCamera.registerCameraAngles([{ name: 'default', x: 0, y: 0, z: 6, zoom: 1, lookAt: new THREE.Vector3(0, 0, 0) }]);
		sCamera.switchCameraAngle('default');

		// Start rendering
		const sTick: TickService = this.core.s('Tick');
		sTick.start();

		//sCamera.setupOrbitControls();
		/*const sDebug = this.core.s('Debug');
		sDebug.addDebugSphere();*/

		// Create a geometry
		const geometry = new THREE.PlaneGeometry(10, 5, 35, 35);
		(geometry as any).originalPositions = geometry.attributes.position.clone();

		// Watch mouse move and do so automatically
		this.autoMouseMove(geometry);
		this.core.canvas.addEventListener('mouseenter', () => this.autoMouseMovePlaying = false);
		this.core.canvas.addEventListener('mouseleave', () => this.autoMouseMovePlaying = true);
		document.addEventListener('mousemove', (event: MouseEvent) => this.mouseMove(event, geometry));

		// Material and mesh
		const material = new THREE.MeshStandardMaterial({ color: 0x616450, side: THREE.DoubleSide, flatShading: true });
		const plane = new THREE.Mesh(geometry, material);
		plane.position.set(0, 0, 0);

		// Add to the scene
		this.core.threeScene.add(plane);

		//sTick.registerCallback({ name: 'shift', callback: () => this.shiftRandomVertices(geometry) });

		// Example: Modify a vertex at (0, 0) with radius 2 and height change 1
		this.shiftRandomVertices(geometry);
	}

	/**
	 * Modifies points and those around
	 */
	modifyVertex(geometry: THREE.BufferGeometry, x: number, y: number, influenceRadius: number = 1.5, heightChange: number = 0.5) {
		const position = geometry.attributes.position; // Access vertex positions
		const vertexCount = position.count; // Total number of vertices

		// Loop through all vertices
		for (let i = 0; i < vertexCount; i++) {
			const vx = position.getX(i); // X position of the vertex
			const vy = position.getY(i); // Y position of the vertex
			const vz = (geometry as any).originalPositions.getZ(i); // Z position of the vertex

			// Calculate distance from the affected point
			const distance = Math.sqrt((vx - x) ** 2 + (vy - y) ** 2);

			// Apply height change with a drop-off based on distance
			if (distance < influenceRadius) {
				const dropoff = 1 - distance / influenceRadius; // Linear drop-off
				const newHeight = vz + heightChange * dropoff; // Adjust height
				position.setZ(i, newHeight); // Update vertex position
			} else {
				position.setZ(i, 0); // Update vertex position
			}
		}

		// Update geometry to reflect changes
		position.needsUpdate = true; // Notify Three.js to update the buffer
		geometry.computeVertexNormals(); // Recalculate normals for lighting
	}

	/**
	 * Shifts all verticies
	 */
	shiftRandomVertices(geometry: THREE.BufferGeometry) {
		const position = geometry.attributes.position; // Access vertex positions
		const vertexCount = position.count; // Total number of vertices
		const maxDistance = 0.2;

		// Loop through all vertices
		for (let i = 0; i < vertexCount; i++) {
			const vx = position.getX(i); // X position of the vertex
			const vy = position.getY(i); // Y position of the vertex
			const vxOriginal = (geometry as any).originalPositions.getX(i);
			//const vyOriginal = (geometry as any).originalPositions.getY(i);

			const randomX = Math.random() / 8 - 0.06;
			const randomY = Math.random() / 8 - 0.06;

			if (this.pointDifference(vx + randomX, vxOriginal) >= maxDistance) return;
			position.setX(i, vx + randomX); // Update vertex position
			position.setY(i, vy + randomY); // Update vertex position

			// Update geometry to reflect changes
			position.needsUpdate = true; // Notify Three.js to update the buffer
			geometry.computeVertexNormals(); // Recalculate normals for lighting
		}
	}

	/**
	 * Calculates the difference between points
	 */
	pointDifference(a: number, b: number) {
		// Regular difference (can be negative)
		const difference = b - a;

		// Absolute difference (always positive)
		const absoluteDifference = Math.abs(difference);

		return absoluteDifference;
	}

	// Sets the directional light
	setDirectionalLight() {
		const sLighting: LightingService = this.core.s('Lighting');
		const directionalLight1 = sLighting.createDirectionalLight("Directional", new THREE.Vector3(40, 10, 20), "#ffffff", 3);
		sLighting.enableLight(directionalLight1);
		sLighting.addShadowsToLight(directionalLight1);
	}

	/**
	 * Detects mouse movement and runs modifyVertex
	 */
	mouseMove(event: MouseEvent, geometry: THREE.PlaneGeometry) {
		const canvasWidth = this.core.canvas.width;
		const canvasHeight = this.core.canvas.height;
		const percX = event.pageX / canvasWidth;
		const percY = event.pageY / canvasHeight;
		
		this.modifyVertex(geometry, percX-0.25, -1*percY*0.75);
	}

	/**
	 * Detects mouse movement and runs modifyVertex
	 */
	autoMouseMove(geometry: THREE.PlaneGeometry) {
		setTimeout(() => this.autoMouseMove(geometry), 50);

		if (this.autoMouseMovePlaying) {
			const xDistance = this.pointDifference(this.autoMouseMovePoint[0], this.autoMouseMoveActivePoints[0][0]);
			const yDistance = this.pointDifference(this.autoMouseMovePoint[1], this.autoMouseMoveActivePoints[0][1]);
			if (xDistance < 0.1 && yDistance < 0.1) {
				this.autoMouseMoveActivePoints.splice(0, 1);
				if (!this.autoMouseMoveActivePoints.length) this.autoMouseMoveActivePoints = JSON.parse(JSON.stringify(this.autoMouseMovePoints));
			}
			
			this.autoMouseMovePoint[0] = this.autoMouseMovePoint[0] + (this.autoMouseMoveActivePoints[0][0] - this.autoMouseMovePoint[0]) / 25;
			this.autoMouseMovePoint[1] = this.autoMouseMovePoint[1] + (this.autoMouseMoveActivePoints[0][1] - this.autoMouseMovePoint[1]) / 25;
			this.modifyVertex(geometry, this.autoMouseMovePoint[0], this.autoMouseMovePoint[1]);
		}
	}
}

interface Triangle {
	mesh: THREE.Mesh;
	originalPosition: THREE.Vector3;
	animatedPosition: THREE.Vector3;
}